import Vue from 'vue'
import Vuex from 'vuex'

import { firestoreAction, vuexfireMutations } from 'vuexfire'

import { Storage } from '@/utils/Storage'
import { db, getAllByIds } from '@/utils/Firebase'
import { reportDiagnostic, isIgnorable } from '@/utils/ErrorReporter'
import { skipsBilling } from '@/utils/Helpers'
import { tierOfAccount } from '@/utils/Plans'
import { preloadExerciseLibrary, invalidateExerciseCache } from '@/utils/ExerciseLibrary'
import { UserIndex } from '@/utils/Algolia'
import { syncHabitTimezone } from '@/utils/Habits'
import { categoryKey, categoryLabel } from '@/utils/Taxonomy'

Vue.use(Vuex)

import moment from 'moment';
import { uniqueDayRoutine } from '../utils/Exercises'

const MAX_MONTHS_PER_FETCH = 12

let initUserTask = null

export default new Vuex.Store({
    state: {

        //Common
        preventRouteLeaveFn: null,
        config: null,
        tutorials: null,
        tutorialsLoaded: false,
        countries: [],
        plans: [],
        groups: [],
        teams: [],
        firstLoadApp: true,
        evaluations: [],
        libraryCoach: null,
        libraryTab: null,
        libraryFolder: null,
        libraryPage: 0,
        exerciseConfigClipboard: null,
        prescriptionClipboard: null,

        //User
        currentUser: null,
        account: null,
        currentSubscription: null,
        brandLogo: null,
        coachMissing: false,
        companyMissing: false,
        coachCompanyBlocked: false,
        planOwner: null,
        planRequired: null,
        redirectRoute: null,

        //Coaches
        filterCoach: '(accountType:coach AND readyToTrain=1) ',

        //Students
        studentInvitations: [],

        //Loading
        loadingUser: false,

        //Dialog
        dialogStack: [],
        blockedDialogs: [],

        //Title
        title: '',
        toolbarMeta: null,
        toolbarSubtitle: '',
        toolbarCoach: null,

        //Routines
        showCalendar: true,
        currentRoutine: null,
        routinesStudent: [],
        groupRoutines: [],
        teamGroupRoutines: [],
        currentCarousel: null,
        coachAssignedRoutines: [],
        folders: [],
        globalFolders: [],
        routineNotes: [],
        exerciseCache: new Map(),
        companyCoaches: null,
        companyCoachesAt: 0,
        companyCoachesId: null,

        //Exercises
        categoriesOptions: [],
        loadingCategories: false,
        equipment: [],

        //Snackbar
        toasts: [],

        //Overlay
        overlay: false,
        overlayLoading: false,
        overlayUpdate: false,
        updateAvailable: false,
    },
    mutations: {

        setPreventRouteLeaveFn(state, fn) {
            state.preventRouteLeaveFn = fn
        },

        setExerciseConfigClipboard(state, config) {
            state.exerciseConfigClipboard = config
        },

        setPrescriptionClipboard(state, prescription) {
            state.prescriptionClipboard = prescription
        },

        setCoachAssignedRoutines(state, routines) {
            state.coachAssignedRoutines = routines
        },

        addCoachAssignedRoutines(state, routines) {
            state.coachAssignedRoutines = [...state.coachAssignedRoutines, ...routines];
        },

        removeCoachAssignedRoutines(state, date) {
            state.coachAssignedRoutines = state.coachAssignedRoutines.filter(routine => routine.rawDate.full !== date);
        },

        resetCoachAssignedRoutines(state) {
            state.coachAssignedRoutines = [];
        },

        setCategoriesOptions(state, options) {
            state.categoriesOptions = options
        },

        setLoadingCategoriesOptions(state, loading = false) {
            state.loadingCategories = loading
        },

        setCurrentUser(state, user) {
            state.currentUser = user
        },

        showSnackBar(state, message = '') {
            const toast = {
                id: `${Date.now()}${Math.random().toString(36).slice(2, 8)}`,
                text: message.text || message,
                color: message.color || '',
            }
            if (!toast.text) return
            state.toasts = [...state.toasts, toast].slice(-3)
        },

        hideSnackBar(state, id = null) {
            state.toasts = id ? state.toasts.filter(toast => toast.id !== id) : []
        },

        clearRoutinesStudent(state) {
            state.routinesStudent = []
        },

        setRoutinesStudent(state, routines) {
            state.routinesStudent = routines
        },

        setGroupRoutines(state, groupRoutines) {
            state.groupRoutines = groupRoutines
        },

        setTeamGroupRoutines(state, teamGroupRoutines) {
            state.teamGroupRoutines = teamGroupRoutines
        },

        setRoutineNotes(state, notes) {
            state.routineNotes = notes
        },

        showOverlay(state, isLoading = false) {
            state.overlay = true
            state.overlayLoading = isLoading
        },

        hideOverlay(state) {
            state.overlay = false
            state.overlayLoading = false
        },

        setOverlayUpdate(state, overlay = false) {
            state.overlayUpdate = overlay
        },

        setUpdateAvailable(state, available = false) {
            state.updateAvailable = available
        },

        setLoadingUser(state, isLoading = false) {
            state.loadingUser = isLoading
        },

        pushDialog(state, id) {
            if (!state.dialogStack.includes(id)) state.dialogStack.push(id)
        },

        removeDialog(state, id) {
            state.dialogStack = state.dialogStack.filter(x => x !== id)
            state.blockedDialogs = state.blockedDialogs.filter(x => x !== id)
        },

        popDialog(state) {
            const last = state.dialogStack[state.dialogStack.length - 1]
            if (last !== undefined && state.blockedDialogs.includes(last)) return
            state.dialogStack = state.dialogStack.slice(0, -1)
        },

        blockDialog(state, id) {
            if (!state.blockedDialogs.includes(id)) state.blockedDialogs.push(id)
        },

        unblockDialog(state, id) {
            state.blockedDialogs = state.blockedDialogs.filter(x => x !== id)
        },

        clearDialogs(state) {
            state.dialogStack = []
            state.blockedDialogs = []
        },

        setShowCalendar(state, show = false) {
            state.showCalendar = show
        },

        setFilterCoach(state, filter = '(accountType:coach AND readyToTrain=1) ') {
            state.filterCoach = filter
        },

        setTitle(state, title = '') {
            state.title = title
        },

        setToolbarMeta(state, meta = null) {
            state.toolbarMeta = meta
        },

        setToolbarSubtitle(state, subtitle = '') {
            state.toolbarSubtitle = subtitle
        },

        setToolbarCoach(state, coach = null) {
            state.toolbarCoach = coach
        },

        setCurrentRoutine(state, routine = null) {
            state.currentRoutine = routine
        },

        setCurrentCarousel(state, carousel = null) {
            state.currentCarousel = carousel
        },

        setFirstLoadApp(state) {
            state.firstLoadApp = false
        },

        setCurrentAccount(state, account) {
            if (!state.account && account) state.account = { ...account }
        },

        setBrandLogo(state, logo = null) {
            state.brandLogo = logo
        },

        setCoachMissing(state, value = false) {
            state.coachMissing = value
        },

        setCompanyMissing(state, value = false) {
            state.companyMissing = value
        },

        setCoachCompanyBlocked(state, value = false) {
            state.coachCompanyBlocked = value
        },

        setPlanOwner(state, account = null) {
            state.planOwner = account ? { ...account } : null
        },

        setTutorials(state, tutorials = null) {
            state.tutorials = tutorials
            state.tutorialsLoaded = true
        },

        showPlanRequired(state, payload = null) {
            state.planRequired = payload ? { ...payload } : null
        },

        hidePlanRequired(state) {
            state.planRequired = null
        },

        setRedirectRoute(state, route = null) {
            state.redirectRoute = route
        },

        setLibraryCoachState(state, coach = null) {
            state.libraryCoach = coach
        },

        setLibraryTab(state, tab = null) {
            state.libraryTab = tab
        },

        setLibraryFolder(state, folder = null) {
            state.libraryFolder = folder
        },

        setLibraryPage(state, libraryPage = 0) {
            state.libraryPage = libraryPage
        },

        setExercisesCache(state, exercises) {
            exercises.forEach(ex => state.exerciseCache.set(ex.objectID, ex));
        },

        setExercisesCacheMisses(state, ids) {
            ids.forEach(id => state.exerciseCache.set(id, null));
        },

        clearExercisesCache(state) {
            state.exerciseCache.clear()
        },

        setCompanyCoaches(state, { coaches, companyId }) {
            state.companyCoaches = coaches
            state.companyCoachesAt = Date.now()
            state.companyCoachesId = companyId || null
        },

        clearCompanyCoaches(state) {
            state.companyCoaches = null
            state.companyCoachesAt = 0
            state.companyCoachesId = null
        },

        updateAssignedQuestionnaireId(state, { groupBy, routineId, questionnaireId }) {
            let list = []
            if (groupBy === 'students') list = state.routinesStudent
            if (groupBy === 'groups') list = state.groupRoutines
            if (groupBy === 'teams') list = state.teamGroupRoutines

            const index = list.findIndex(r => r.id === routineId)
            if (index >= 0) Vue.set(list[index], 'assignedQuestionnaireId', questionnaireId)
        },

        ...vuexfireMutations,
    },
    actions: {

        async setCurrentUser({ commit }, user) {
            const cleanUser = user ? JSON.parse(JSON.stringify(user)) : null
            commit('setCurrentUser', cleanUser)
            await Storage.setUser(cleanUser)
        },

        async setCurrentAccount({ commit }, account) {
            commit('setCurrentAccount', account)
            if (account && account.accountType) {
                await Storage.setAccount({ ...account })
            }
        },

        async cleanDataStorage() {
            await Storage.setUser(null)
            await Storage.setAccount(null)
        },

        async getUserAccount({ commit, dispatch }) {
            const user = await Storage.getUser()
            await dispatch('setCurrentUser', user)
            const account = await Storage.getAccount()
            await dispatch('setCurrentAccount', account)
            const brandLogo = await Storage.getBrandLogo()
            commit('setBrandLogo', brandLogo)
            await dispatch('checkCompanyMissing')
        },

        async fetchTutorials({ state, commit }, force = false) {
            if (state.tutorialsLoaded && !force) return state.tutorials
            try {
                const ref = await db.collection('config').doc('tutorials').get()
                commit('setTutorials', ref.exists ? ref.data() : null)
            } catch (e) {
                console.log('Error has occurred:', e.message)
                commit('setTutorials', null)
            }
            return state.tutorials
        },

        async fetchCompanyCoaches({ state, commit }, companyId) {
            if (!companyId) return []
            const ttl = 5 * 60 * 1000
            const cached = state.companyCoaches
                && state.companyCoachesId === companyId
                && (Date.now() - state.companyCoachesAt) < ttl
            if (cached) return state.companyCoaches
            try {
                const { hits } = await UserIndex.search('', {
                    filters: `coachSubscription.companyId: ${companyId} AND coachSubscription.paused != 1`,
                    hitsPerPage: 1000,
                })
                commit('setCompanyCoaches', { coaches: hits, companyId })
                return hits
            } catch (e) {
                console.log('Error has occurred:', e.message)
                return state.companyCoachesId === companyId ? (state.companyCoaches || []) : []
            }
        },

        async fetchExercisesCache({ state, commit }, allIds) {
            const missingIds = allIds.filter(id => !state.exerciseCache.has(id));
            if (missingIds.length === 0) return allIds.map(id => state.exerciseCache.get(id))

            let failure = null

            try {
                commit('setExercisesCache', await getAllByIds('exercises', missingIds));
            } catch (e) {
                failure = e
                console.log('Error has occurred:', e.message);
            }

            const stillMissing = allIds.filter(id => !state.exerciseCache.has(id));
            if (stillMissing.length) {
                try {
                    commit('setExercisesCache', await getAllByIds('exercises', stillMissing, 10, 20, 'server'));
                    commit('setExercisesCacheMisses', stillMissing.filter(id => !state.exerciseCache.has(id)));
                } catch (e) {
                    failure = e
                    console.log('Error has occurred:', e.message);
                }
            }

            const deleted = allIds.filter(id => state.exerciseCache.get(id) === null)
            const unreadable = allIds.filter(id => !state.exerciseCache.has(id))
            const failureMessage = failure ? `${failure.code || ''} ${failure.message || ''}`.trim() : ''
            if (unreadable.length && !isIgnorable(failureMessage)) {
                reportDiagnostic({
                    title: 'Ejercicios sin leer: fallo la consulta a exercises',
                    fields: {
                        Pedidos: allIds.length,
                        Faltantes: missingIds.length,
                        'Sin leer': unreadable.length,
                        Borrados: deleted.length,
                        Error: failureMessage || 'sin excepcion',
                        Ejemplo: unreadable.slice(0, 3).join(', '),
                    },
                })
            }

            return allIds.map(id => state.exerciseCache.get(id));
        },

        async fetchRoutinesStudent({ commit }, data) {
            if (!data.studentId) return

            const queryMonth = (m) => db.collection('assignedRoutines')
                .where('studentId', '==', data.studentId)
                .where('rawDate.month', "==", m.format('MM'))
                .where('rawDate.year', "==", m.format('YYYY'))
                .orderBy('rawDate.full', 'asc')
                .get()

            const from = moment(data.date).startOf('month')
            const to = data.endDate ? moment(data.endDate).startOf('month') : from.clone()
            const start = moment.min(from, to)
            const end = moment.max(from, to)
            const months = []
            const cursor = start.clone()
            while (cursor.isSameOrBefore(end, 'month') && months.length < MAX_MONTHS_PER_FETCH) {
                months.push(cursor.clone())
                cursor.add(1, 'month')
            }

            const refs = await Promise.all(months.map(queryMonth))
            const docs = refs.flatMap(ref => ref.docs.map(doc => ({ id: doc.id, ...doc.data() })))

            await commit('setRoutinesStudent', uniqueDayRoutine(docs))
        },

        async fetchGroupRoutines({ commit }, data) {
            if (!data.groupId) return
            let groupRoutines = []
            // console.log('querying group routines')

            let startDate = data.date
            let endDate = moment(data.date).add(1, 'month').format('YYYY-MM-DD')

            await db.collection('groupAssignedRoutines')
                .where('groupId', '==', data.groupId)
                .where('rawDate.full', ">=", startDate)
                .where('rawDate.full', "<=", endDate)
                .orderBy('rawDate.full', 'asc').get().then(routines => {
                    routines.forEach(routine => {
                        groupRoutines.push({
                            id: routine.id,
                            ...routine.data()
                        })
                    })
                })

            // console.log('groupRoutines', groupRoutines)

            await commit('setGroupRoutines', uniqueDayRoutine(groupRoutines))
        },

        async fetchTeamGroupRoutines({ commit }, data) {
            if (!data.teamId || data.groupId < 0) return
            let teamGroupRoutines = []
            // console.log('querying team group routines')

            let startDate = data.date
            let endDate = moment(data.date).add(1, 'month').format('YYYY-MM-DD')

            await db.collection('groupAssignedRoutines')
                .where('teamId', '==', data.teamId)
                .where('groupId', '==', data.groupId)
                .where('rawDate.full', ">=", startDate)
                .where('rawDate.full', "<=", endDate)
                .orderBy('rawDate.full', 'asc').get().then(routines => {
                    routines.forEach(routine => {
                        teamGroupRoutines.push({
                            id: routine.id,
                            ...routine.data()
                        })
                    })
                })

            // console.log('teamGroupRoutines', teamGroupRoutines)

            await commit('setTeamGroupRoutines', uniqueDayRoutine(teamGroupRoutines))
        },

        async fetchRoutineNotes({ commit }, data) {
            let date = moment(data.date)
            let ref = await db.collection('routineNotes')
                .where('studentId', '==', data.studentId)
                .where('groupId', '==', data.groupId)
                .where('teamId', '==', data.teamId)
                .where('rawDate.month', "==", date.format('MM'))
                .where('rawDate.year', "==", date.format('YYYY'))
                .orderBy('date', 'asc')
                .get()

            await commit('setRoutineNotes', ref.docs.map(doc => ({ id: doc.id, ...doc.data() })))
        },

        async fetchExerciseCategories({ commit }) {
            let categoriesOptions = []
            commit('setLoadingCategoriesOptions', true)
            await db.collection('categories').get().then(categories => {
                categories.docs.forEach(category => {
                    categoriesOptions.push({
                        id: category.id,
                        value: category.data().name,
                        key: category.data().key || categoryKey(category.data().name),
                        label: categoryLabel(category.data().name),
                        ...category.data(),
                    })
                })
            })
            commit('setCategoriesOptions', categoriesOptions)
            commit('setLoadingCategoriesOptions')
        },

        async fetchCoachAssignedRoutines({ commit }, data) {
            if (!data?.studentId) {
                commit('setCoachAssignedRoutines', [])
                return;
            }
            let startDate = moment(data.date).format('YYYY-MM-DD')
            let endDate = moment(data.date).add(1, 'month').format('YYYY-MM-DD')
            if (data.endDate) endDate = data.endDate
            let ref = await db.collection('assignedRoutines')
                .where('studentId', '==', data.studentId)
                .where('rawDate.full', ">=", startDate)
                .where('rawDate.full', "<=", endDate)
                .orderBy('rawDate.full', 'asc')
                .get()

            commit('setCoachAssignedRoutines', ref.docs.map(doc => ({ id: doc.id, ...doc.data() })))
        },

        async fetchCoachAssignedRoutinesOfDay({ commit }, data) {
            let startDate = moment(data.date).format('YYYY-MM-DD')
            let ref = await db.collection('assignedRoutines')
                .where('coachId', '==', data.coachId)
                .where('rawDate.full', "==", startDate)
                .orderBy('rawDate.full', 'asc')
                .get()

            commit('addCoachAssignedRoutines', ref.docs.map(doc => ({ id: doc.id, ...doc.data() })))
        },

        initUser({ state, commit, dispatch }, user) {
            if (initUserTask && initUserTask.uid === user.uid) return initUserTask.promise
            const promise = (async () => {
                commit('setLoadingUser', true)
                await dispatch('setCurrentUser', user)
                await dispatch('bindUserAccount', user)
                const ownsLibrary = state.account?.accountType !== 'company'
                await Promise.all([
                    dispatch('bindStudentInvitations', user.email),
                    ownsLibrary ? dispatch('bindGroups', user.uid) : Promise.resolve(),
                    ownsLibrary ? dispatch('bindTeams', user.uid) : Promise.resolve(),
                    ownsLibrary ? dispatch('bindFolders', user.uid) : Promise.resolve(),
                    dispatch('bindEvaluations', user.uid),
                ])
                // Precarga la biblioteca de ejercicios para los dialogos de armado de rutina.
                // No bloqueante: si falla, los dialogos cargan on-demand.
                preloadExerciseLibrary(user)
                dispatch('preloadBrandLogo')
                if (state.account?.accountType === 'student' && state.account?.studentSubscription?.coachId) {
                    syncHabitTimezone(user.uid).catch(err => console.log('syncHabitTimezone', err))
                }
                await dispatch('checkCompanyMissing')
                commit('setLoadingUser', false)
            })().finally(() => {
                if (initUserTask && initUserTask.promise === promise) initUserTask = null
            })
            initUserTask = { uid: user.uid, promise }
            return promise
        },

        async preloadBrandLogo({ state, commit }) {
            const isStudent = state.account?.accountType === 'student'
            const coachId = state.account?.studentSubscription?.coachId || null
            if (!coachId) {
                commit('setCoachMissing', false)
                if (isStudent) commit('setPlanOwner', null)
                await Storage.setBrandLogo(null)
                return commit('setBrandLogo', null)
            }
            try {
                const coachRef = await db.collection('users').doc(coachId).get()
                commit('setCoachMissing', !coachRef.exists)
                if (isStudent) {
                    let owner = coachRef.exists ? coachRef.data() : null
                    const companyId = owner?.coachSubscription?.companyId || null
                    if (owner && companyId && !tierOfAccount(owner)) {
                        const companyRef = await db.collection('users').doc(companyId).get()
                        if (companyRef.exists) owner = companyRef.data()
                    }
                    commit('setPlanOwner', owner)
                }
                const logo = coachRef.data()?.logo?.url || null
                commit('setBrandLogo', logo)
                await Storage.setBrandLogo(logo)
            } catch (error) {
                console.error('Error preloading brand logo:', error)
            }
        },

        async checkCompanyMissing({ state, commit }) {
            const account = state.account || null
            const companyId = account?.coachSubscription?.companyId || null
            if (account?.accountType !== 'coach_company' || !companyId) {
                commit('setCompanyMissing', false)
                if (account?.accountType === 'coach_company') commit('setPlanOwner', null)
                return commit('setCoachCompanyBlocked', false)
            }
            try {
                const companyRef = await db.collection('users').doc(companyId).get()
                commit('setCompanyMissing', !companyRef.exists)
                if (!companyRef.exists) {
                    commit('setPlanOwner', null)
                    return commit('setCoachCompanyBlocked', false)
                }
                const company = companyRef.data()
                commit('setPlanOwner', company)
                const companyHasAccess = skipsBilling(company) || !!company?.stripeAccount?.currentSubscription?.id
                commit('setCoachCompanyBlocked', !skipsBilling(account) && !companyHasAccess)
            } catch (error) {
                console.error('Error checking company:', error)
            }
        },

        bindStudentInvitations: firestoreAction(async ({ bindFirestoreRef }, email) => {
            return await bindFirestoreRef(
                'studentInvitations',
                db.collection('studentInvitations')
                    .where('studentEmail', '==', email)
                    .orderBy('createdAt', 'desc')
            )
        }),

        bindSubscription: firestoreAction(async ({ bindFirestoreRef }, subscriptionId) => {
            if (!subscriptionId) return null
            return await bindFirestoreRef(
                'currentSubscription',
                db.collection('subscriptions').doc(subscriptionId)
            )
        }),

        bindUserAccount: firestoreAction(async ({ bindFirestoreRef }, user) => {
            return await bindFirestoreRef(
                'account',
                db.collection('users').doc(user.uid)
            ).then(account => {
                Storage.setAccount(account)
            })
        }),

        unbindUserAccount: firestoreAction(({ commit, unbindFirestoreRef }) => {
            commit('clearCompanyCoaches')
            commit('clearExercisesCache')
            commit('setPlanOwner', null)
            invalidateExerciseCache()
            const refs = [
                'account', 'studentInvitations', 'groups', 'teams', 'folders',
                'evaluations', 'currentSubscription'
            ]
            refs.forEach(ref => {
                try { unbindFirestoreRef(ref, true) } catch (e) { /* no estaba bindeado */ }
            })
            commit('clearRoutinesStudent')
            commit('resetCoachAssignedRoutines')
        }),

        bindEquipment: firestoreAction(async ({ bindFirestoreRef }) => {
            return await bindFirestoreRef(
                'equipment',
                db.collection('equipment')
            )
        }),

        bindConfig: firestoreAction(({ bindFirestoreRef }) => {
            return bindFirestoreRef(
                'config',
                db.collection('config').doc('builder')
            )
        }),

        bindCountries: firestoreAction(({ bindFirestoreRef }) => {
            return bindFirestoreRef(
                'countries',
                db.collection('countries').orderBy('name')
            )
        }),

        bindPlans: firestoreAction(({ bindFirestoreRef }) => {
            return bindFirestoreRef(
                'plans',
                db.collection('plans').where('type', '==', 'coach').where('active', '==', true).orderBy('amount')
            )
        }),

        bindCompanyPlans: firestoreAction(({ bindFirestoreRef }) => {
            return bindFirestoreRef(
                'plans',
                db.collection('plans').where('type', '==', 'company').where('active', '==', true).orderBy('amount')
            )
        }),

        bindGroups: firestoreAction(async ({ bindFirestoreRef, unbindFirestoreRef }, coachId) => {
            if (!coachId) {
                unbindFirestoreRef('groups', true)
                return
            }
            await bindFirestoreRef(
                'groups',
                db.collection('groups')
                    .where('coachId', '==', coachId)
            )
        }),

        bindTeams: firestoreAction(async ({ bindFirestoreRef, unbindFirestoreRef }, coachId) => {
            if (!coachId) {
                unbindFirestoreRef('teams', true)
                return
            }
            await bindFirestoreRef(
                'teams',
                db.collection('teams')
                    .where('coachId', '==', coachId)
            )
        }),

        async setLibraryCoach({ commit, dispatch }, coach = null) {
            const coachId = coach?.id || coach?.objectID || null
            commit('setLibraryCoachState', coach ? { ...coach, id: coachId } : null)
            await Promise.all([
                dispatch('bindGroups', coachId),
                dispatch('bindTeams', coachId),
                dispatch('bindFolders', coachId),
            ])
        },

        bindFolders: firestoreAction(async ({ bindFirestoreRef, unbindFirestoreRef }, coachId) => {
            if (!coachId) {
                unbindFirestoreRef('folders', true)
                return
            }
            await bindFirestoreRef(
                'folders',
                db.collection('folders')
                    .where('coachId', '==', coachId)
                    .orderBy('createdAt', 'desc')
            )
        }),

        bindGlobalFolders: firestoreAction(async ({ bindFirestoreRef }) => {
            await bindFirestoreRef(
                'globalFolders',
                db.collection('folders')
                    .where('coachId', '==', '')
                    .orderBy('createdAt', 'desc')
            )
        }),

        bindEvaluations: firestoreAction(async ({ bindFirestoreRef }, coachId) => {
            if (!coachId) return
            await bindFirestoreRef(
                'evaluations',
                db.collection('evaluations')
                    .where('coachId', '==', coachId)
            )
        }),

    },
})
